/**
 * NotificationBanner - Reusable compact banner for displaying notification alerts
 *
 * @param {string} title - Main title text
 * @param {string} subtitle - Subtitle/description text
 * @param {string} severity - 'error', 'warning', 'info', or 'success'
 * @param {function} onResolve - Callback when resolve button is clicked
 * @param {string} resolveText - Text for the resolve button (default: "Resolve")
 */
export default function NotificationBanner({
  title,
  subtitle,
  severity = 'warning',
  onResolve,
  resolveText = 'Resolve'
}) {
  // Get gradient and icon based on severity
  const getSeverityConfig = () => {
    switch (severity) {
      case 'error':
        return {
          gradient: 'from-red-600 to-orange-600',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )
        };
      case 'warning':
        return {
          gradient: 'from-yellow-600 to-orange-600',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )
        };
      case 'info':
        return {
          gradient: 'from-blue-600 to-purple-600',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      case 'success':
        return {
          gradient: 'from-green-600 to-emerald-600',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
      default:
        return {
          gradient: 'from-gray-600 to-gray-700',
          icon: (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        };
    }
  };

  const config = getSeverityConfig();

  return (
    <div className="px-3 pt-1 flex-shrink-0">
      <div className={`relative bg-gradient-to-r ${config.gradient} rounded-lg p-2.5 shadow-lg border border-white/10 mb-0.5`} style={{ color: 'var(--color-text-on-accent)' }}>
              {/* Content */}
        <div className="relative flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {/* Icon */}
            <div className="flex-shrink-0" style={{ color: 'var(--color-text-on-accent)' }}>
              {config.icon}
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-xs leading-tight truncate" style={{ color: 'var(--color-text-on-accent)' }}>
                {title}
              </div>
              {subtitle && (
                <div className="text-[10px] leading-tight mt-0.5 line-clamp-2" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          {/* Resolve Button - Compact */}
          {onResolve && (
            <button
              onClick={onResolve}
              className="px-2.5 py-1.5 bg-white/20 hover:bg-white/30 font-medium rounded text-xs whitespace-nowrap transition-all duration-200 border border-white/30 hover:border-white/50 flex-shrink-0"
              style={{ color: 'var(--color-text-on-accent)' }}
            >
              {resolveText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
